import { useEffect, useId, useRef, useState, type ReactNode } from 'react';
import { FiCheck, FiChevronDown, FiSearch, FiX } from 'react-icons/fi';
import './TableFilters.css';

export interface FilterOption {
  value: string;
  label: string;
  description?: string;
  badge?: string;
  disabled?: boolean;
}

interface BaseField {
  id: string;
  label: string;
  active: boolean;
  onClear: () => void;
  /** Override the selected-condition chip, e.g. a custom range summary. */
  summary?: string;
}

interface SelectField extends BaseField {
  type: 'select';
  value: string;
  options: FilterOption[];
  onChange: (value: string) => void;
}

interface MultiSelectField extends BaseField {
  type: 'multi-select';
  value: string[];
  options: FilterOption[];
  onChange: (value: string[]) => void;
  placeholder?: string;
  searchable?: boolean;
  /** Supply both for remote search; fetching and debouncing belong to the caller. */
  search?: string;
  onSearchChange?: (query: string) => void;
  loading?: boolean;
  error?: string;
  onRetry?: () => void;
  emptyMessage?: string;
  minSelected?: number;
}

interface InputField extends BaseField {
  type: 'text' | 'number' | 'date';
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
}

interface CustomField extends BaseField {
  type: 'custom';
  render: (controlId: string) => ReactNode;
}

export type TableFilterField = SelectField | MultiSelectField | InputField | CustomField;

function fieldSummary(field: TableFilterField): string {
  if (field.summary !== undefined) return field.summary;
  if (field.type === 'custom') return 'Selected';
  if (field.type === 'multi-select') {
    if (field.value.length !== 1) return `${field.value.length} selected`;
    return field.options.find(option => option.value === field.value[0])?.label || field.value[0];
  }
  if (field.type === 'select') return field.options.find(option => option.value === field.value)?.label || field.value;
  return field.value;
}

function MultiSelect({ field, controlId }: { field: MultiSelectField; controlId: string }) {
  const [open, setOpen] = useState(false);
  const [localSearch, setLocalSearch] = useState('');
  const root = useRef<HTMLDivElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const searchInput = useRef<HTMLInputElement>(null);
  const search = field.search ?? localSearch;
  const setSearch = field.onSearchChange ?? setLocalSearch;
  const searchable = field.searchable !== false;
  const selected = new Set(field.value);
  const query = search.trim().toLowerCase();
  // Remote searches may match attributes absent from the returned label (e.g. email).
  const options = field.onSearchChange ? field.options : field.options.filter(option =>
    `${option.label} ${option.description || ''} ${option.value}`.toLowerCase().includes(query));

  useEffect(() => {
    if (!open) return;
    if (searchable) searchInput.current?.focus();
    const closeOutside = (event: PointerEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', closeOutside);
    return () => document.removeEventListener('pointerdown', closeOutside);
  }, [open, searchable]);

  return (
    <div ref={root} className="tf-multi" onBlur={event => {
      if (event.relatedTarget && !event.currentTarget.contains(event.relatedTarget as Node)) setOpen(false);
    }} onKeyDown={event => {
      if (event.key === 'Escape' && open) {
        event.stopPropagation(); setOpen(false); trigger.current?.focus();
      }
    }}>
      <button ref={trigger} id={controlId} type="button" className={`tf-control tf-trigger${field.active ? ' tf-selected' : ''}`}
        aria-expanded={open} aria-controls={`${controlId}-options`} aria-labelledby={`${controlId}-label ${controlId}-value`}
        onClick={() => { setOpen(previous => !previous); if (!open) setSearch(''); }}>
        <span id={`${controlId}-value`} className="tf-value">{field.active ? fieldSummary(field) : field.placeholder || 'All'}</span>
        <FiChevronDown aria-hidden className={open ? 'tf-chevron-open' : ''} />
      </button>
      {open && <div id={`${controlId}-options`} className="tf-popover" role="group" aria-labelledby={`${controlId}-label`}>
        {searchable && <div className="tf-search"><FiSearch aria-hidden /><input type="text" ref={searchInput} aria-label={`Search ${field.label}`} placeholder={`Search ${field.label.toLowerCase()}…`} value={search} onChange={event => setSearch(event.target.value)} /></div>}
        <div className="tf-options">
          {field.error ? <div className="tf-message" role="alert">{field.error}{field.onRetry && <button type="button" onClick={field.onRetry}>Retry</button>}</div>
            : field.loading ? <div className="tf-message" role="status">Searching…</div>
              : options.length === 0 ? <div className="tf-message" role="status">{field.emptyMessage || 'No matching options'}</div>
                : options.map(option => <label key={option.value} className="tf-option">
                  <input type="checkbox" checked={selected.has(option.value)} disabled={option.disabled || (selected.has(option.value) && field.value.length <= (field.minSelected ?? 0))}
                    onChange={() => field.onChange(selected.has(option.value) ? field.value.filter(value => value !== option.value) : [...field.value, option.value])} />
                  <span className="tf-option-copy"><span>{option.label}</span>{option.description && <small>{option.description}</small>}</span>
                  {option.badge && <span className="tf-badge">{option.badge}</span>}
                </label>)}
        </div>
        <div className="tf-popover-footer"><span>{field.value.length} selected</span><button type="button" onClick={() => { setOpen(false); trigger.current?.focus(); }}><FiCheck aria-hidden /> Done</button></div>
      </div>}
    </div>
  );
}

function Field({ field }: { field: TableFilterField }) {
  const controlId = useId();
  return <div className="tf-field">
    <label className="tf-label" id={`${controlId}-label`} htmlFor={controlId}>{field.label}</label>
    {field.type === 'multi-select' ? <MultiSelect field={field} controlId={controlId} />
      : field.type === 'select' ? <select id={controlId} className={`tf-control${field.active ? ' tf-selected' : ''}`} value={field.value} onChange={event => field.onChange(event.target.value)}>
        {field.options.map(option => <option key={option.value} value={option.value} disabled={option.disabled}>{option.label}</option>)}
      </select>
        : field.type === 'custom' ? field.render(controlId)
          : <input id={controlId} className="tf-control" type={field.type} value={field.value} placeholder={field.placeholder} onChange={event => field.onChange(event.target.value)} />}
  </div>;
}

/** Controlled presentation only: table-specific state, defaults and API queries stay in the page. */
export default function TableFilters({ fields, onClearAll, label = 'Table filters' }: {
  fields: TableFilterField[];
  onClearAll: () => void;
  label?: string;
}) {
  const active = fields.filter(field => field.active);
  return <section className="table-filters" aria-label={label}>
    <div className="tf-fields">{fields.map(field => <Field key={field.id} field={field} />)}</div>
    <div className="tf-summary">
      <div className="tf-chips">{active.length ? active.map(field => <button key={field.id} type="button" className="tf-chip" onClick={field.onClear} aria-label={`Clear ${field.label}`}>
        <span>{field.label}: <strong>{fieldSummary(field)}</strong></span><FiX aria-hidden />
      </button>) : <span className="tf-hint">No filters applied</span>}</div>
      <button className="tf-clear" type="button" onClick={onClearAll} disabled={!active.length}>Clear all</button>
    </div>
  </section>;
}
